// ============================================================================
// Marketing / Google Ads: fonte unica di verità per
//   - attribuzione (UTM + click id: gclid, wbraid, gbraid)
//   - tassonomia conversioni (micro vs economiche)
//   - registro funnel verticali (landing con form in pagina)
//   - piano campagne Search (keyword amministrabili, NON auto-attivate)
// Importato sia dal client (form, landing) sia dal server (validazione,
// persistenza, centrale operativa marketing).
// ============================================================================

import { z } from "zod";

// ---------------------------------------------------------------------------
// Attribuzione
// ---------------------------------------------------------------------------

const shortStr = z.string().trim().max(200);

export const attributionSchema = z
  .object({
    utmSource: shortStr.optional(),
    utmMedium: shortStr.optional(),
    utmCampaign: shortStr.optional(),
    utmTerm: shortStr.optional(),
    utmContent: shortStr.optional(),
    gclid: shortStr.optional(),
    wbraid: shortStr.optional(),
    gbraid: shortStr.optional(),
    landingPage: z.string().trim().max(500).optional(),
    referrer: z.string().trim().max(500).optional(),
    // ISO timestamp del primo atterraggio (first touch), impostato dal client.
    firstTouchAt: z.string().trim().max(40).optional(),
  })
  .strip();

export type Attribution = z.infer<typeof attributionSchema>;

/** Ripulisce un payload attribuzione arbitrario; null se vuoto/invalid. */
export function sanitizeAttribution(raw: unknown): Attribution | null {
  const parsed = attributionSchema.safeParse(raw);
  if (!parsed.success) return null;
  const a = parsed.data;
  const hasAny = Object.values(a).some((v) => typeof v === "string" && v.length > 0);
  return hasAny ? a : null;
}

/** Il lead ha un click id utilizzabile per le conversioni offline Google Ads? */
export function hasAdsClickId(a: Attribution | null | undefined): boolean {
  return !!(a && (a.gclid || a.wbraid || a.gbraid));
}

// ---------------------------------------------------------------------------
// Tassonomia conversioni
// ---------------------------------------------------------------------------
// MICRO: segnali di interesse, non fatturabili. ECONOMICHE: passi della
// pipeline commerciale con (eventuale) valore in euro. La distinzione guida
// sia gli eventi GA4 sia l'export conversioni offline verso Google Ads.

export const MICRO_CONVERSIONS = [
  "form_start",
  "roi_calculator_start",
  "erp_config_start",
  "erp_config_step",
  "erp_config_contact_saved",
  "erp_config_abandoned",
] as const;

export const ECONOMIC_CONVERSIONS = [
  "richiesta_perizia",
  "richiesta_audit_ads",
  "preventivo_qualificato",
  "appuntamento_prenotato",
  "lead_qualificato",
  "proposta_inviata",
  "incarico_firmato",
  "valore_acquisito",
  "erp_config_completed",
  "erp_demo_richiesta",
  "valutazione_tecnica_ai",
  "lead_funnel",
] as const;

export type MicroConversion = (typeof MICRO_CONVERSIONS)[number];
export type EconomicConversion = (typeof ECONOMIC_CONVERSIONS)[number];
export type ConversionName = MicroConversion | EconomicConversion;

export const ALL_CONVERSIONS = [
  ...MICRO_CONVERSIONS,
  ...ECONOMIC_CONVERSIONS,
] as const;

export function isEconomicConversion(name: string): boolean {
  return (ECONOMIC_CONVERSIONS as readonly string[]).includes(name);
}

// Pipeline commerciale dei lead funnel: ogni transizione registra la
// conversione economica corrispondente (dedupe su lead+evento).
export const FUNNEL_LEAD_STATUSES = [
  "nuovo",
  "qualificato",
  "call_prenotata",
  "proposta_inviata",
  "incarico_firmato",
  "perso",
] as const;
export type FunnelLeadStatus = (typeof FUNNEL_LEAD_STATUSES)[number];

export const STATUS_TO_CONVERSION: Partial<Record<FunnelLeadStatus, EconomicConversion>> = {
  qualificato: "lead_qualificato",
  call_prenotata: "appuntamento_prenotato",
  proposta_inviata: "proposta_inviata",
  incarico_firmato: "incarico_firmato",
};

// ---------------------------------------------------------------------------
// Registro funnel verticali
// ---------------------------------------------------------------------------
// Ogni funnel ha: route landing, servizio del preventivo collegato, evento di
// conversione primaria, campagna Search di appartenenza (per isolamento), e
// campi brief specifici (validati in briefSchemaFor).

export const FUNNEL_IDS = [
  "perizia-generale",
  "perizia-conferimento",
  "perizia-cessione",
  "perizia-royalty",
  "audit-google-ads",
  "crm-pmi",
  "portale-clienti",
  "gestionale-su-misura",
  "ai-valutazione",
] as const;
export type FunnelId = (typeof FUNNEL_IDS)[number];

export interface FunnelDef {
  id: FunnelId;
  path: string;
  thanksPath: string;
  label: string;
  service: string; // valore preselezionato per /preventivo?service=
  campaign: CampaignId | null;
  primaryConversion: EconomicConversion;
}

export const CAMPAIGN_IDS = [
  "perizia-marchio",
  "audit-google-ads",
  "crm-software",
] as const;
export type CampaignId = (typeof CAMPAIGN_IDS)[number];

export const CAMPAIGNS: Record<CampaignId, { label: string }> = {
  "perizia-marchio": { label: "Perizia e valorizzazione marchio" },
  "audit-google-ads": { label: "Audit e consulenza Google Ads" },
  "crm-software": { label: "CRM e software personalizzato" },
};

export const FUNNELS: readonly FunnelDef[] = [
  {
    id: "perizia-generale",
    path: "/valorizzazione-marchio/perizia",
    thanksPath: "/grazie/perizia-generale",
    label: "Valutazione / perizia marchio",
    service: "Perizie giurate e valutazioni marchi",
    campaign: "perizia-marchio",
    primaryConversion: "richiesta_perizia",
  },
  {
    id: "perizia-conferimento",
    path: "/valorizzazione-marchio/conferimento",
    thanksPath: "/grazie/perizia-conferimento",
    label: "Perizia marchio per conferimento",
    service: "Perizie giurate e valutazioni marchi",
    campaign: "perizia-marchio",
    primaryConversion: "richiesta_perizia",
  },
  {
    id: "perizia-cessione",
    path: "/valorizzazione-marchio/cessione",
    thanksPath: "/grazie/perizia-cessione",
    label: "Perizia marchio per cessione o trasferimento",
    service: "Perizie giurate e valutazioni marchi",
    campaign: "perizia-marchio",
    primaryConversion: "richiesta_perizia",
  },
  {
    id: "perizia-royalty",
    path: "/valorizzazione-marchio/royalty",
    thanksPath: "/grazie/perizia-royalty",
    label: "Determinazione canone di licenza / royalty",
    service: "Perizie giurate e valutazioni marchi",
    campaign: "perizia-marchio",
    primaryConversion: "richiesta_perizia",
  },
  {
    id: "audit-google-ads",
    path: "/audit-google-ads",
    thanksPath: "/grazie/audit-google-ads",
    label: "Audit Google Ads",
    service: "SEM",
    campaign: "audit-google-ads",
    primaryConversion: "richiesta_audit_ads",
  },
  {
    id: "crm-pmi",
    path: "/servizi/crm-pmi",
    thanksPath: "/grazie/crm-pmi",
    label: "CRM personalizzato per PMI",
    service: "CRM",
    campaign: "crm-software",
    primaryConversion: "lead_funnel",
  },
  {
    id: "portale-clienti",
    path: "/servizi/portale-clienti",
    thanksPath: "/grazie/portale-clienti",
    label: "Portale clienti e fornitori",
    service: "Creazione portali",
    campaign: "crm-software",
    primaryConversion: "lead_funnel",
  },
  {
    id: "gestionale-su-misura",
    path: "/servizi/gestionale-su-misura",
    thanksPath: "/grazie/gestionale-su-misura",
    label: "Gestionale su misura",
    service: "Gestionali",
    campaign: "crm-software",
    primaryConversion: "lead_funnel",
  },
  {
    id: "ai-valutazione",
    path: "/servizi/intelligenza-artificiale",
    thanksPath: "/grazie/ai-valutazione",
    label: "Valutazione tecnica AI di processo",
    service: "Sviluppo con IA",
    campaign: null,
    primaryConversion: "valutazione_tecnica_ai",
  },
] as const;

export function getFunnel(id: string): FunnelDef | undefined {
  return FUNNELS.find((f) => f.id === id);
}

// ---------------------------------------------------------------------------
// Brief qualificante per funnel (campi specifici, tutti amministrabili in
// lettura dalla scheda lead admin). Il brief è validato lato server.
// ---------------------------------------------------------------------------

const briefBase = {
  urgenza: z.enum(["urgente", "1-3-mesi", "oltre-3-mesi", "da-definire"]).optional(),
  note: z.string().trim().max(1500).optional(),
};

const periziaBrief = z
  .object({
    ...briefBase,
    finalita: z
      .enum(["valutazione", "conferimento", "cessione", "royalty", "contenzioso", "altro"])
      .optional(),
    documentiDisponibili: z.array(z.string().trim().max(120)).max(20).optional(),
    soggettiCoinvolti: z.string().trim().max(300).optional(),
    marchioRegistrato: z.enum(["si", "no", "in-corso", "non-so"]).optional(),
  })
  .strip();

const auditAdsBrief = z
  .object({
    ...briefBase,
    spesaMensile: z
      .enum(["sotto-1000", "1000-3000", "3000-10000", "oltre-10000", "non-attiva"])
      .optional(),
    obiettivo: z.string().trim().max(300).optional(),
    accessoAccount: z.enum(["si", "no", "da-verificare"]).optional(),
  })
  .strip();

const softwareBrief = z
  .object({
    ...briefBase,
    settore: z.string().trim().max(120).optional(),
    problemaOperativo: z.string().trim().max(600).optional(),
    numeroUtenti: z.enum(["1-5", "6-15", "16-50", "oltre-50"]).optional(),
    strumentiAttuali: z.string().trim().max(300).optional(),
    integrazioni: z.string().trim().max(300).optional(),
    budget: z
      .enum(["sotto-5000", "5000-15000", "15000-50000", "oltre-50000", "da-definire"])
      .optional(),
    tempi: z.string().trim().max(120).optional(),
  })
  .strip();

const aiBrief = z
  .object({
    ...briefBase,
    casoUso: z
      .enum([
        "automazione-documentale",
        "ai-crm-gestionale",
        "assistente-amministrativo",
        "estrazione-dati",
        "chatbot-operativo",
        "poc",
        "altro",
      ])
      .optional(),
    processoDaValutare: z.string().trim().max(600).optional(),
    volumi: z.string().trim().max(200).optional(),
  })
  .strip();

export function briefSchemaFor(funnel: FunnelId) {
  switch (funnel) {
    case "perizia-generale":
    case "perizia-conferimento":
    case "perizia-cessione":
    case "perizia-royalty":
      return periziaBrief;
    case "audit-google-ads":
      return auditAdsBrief;
    case "crm-pmi":
    case "portale-clienti":
    case "gestionale-su-misura":
      return softwareBrief;
    case "ai-valutazione":
      return aiBrief;
  }
}

// Payload pubblico POST /api/funnel-lead
export const insertFunnelLeadSchema = z.object({
  funnel: z.enum(FUNNEL_IDS),
  name: z.string().trim().min(2, "Inserisci il tuo nome").max(120),
  email: z.string().trim().email("Inserisci un indirizzo email valido").max(160),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  company: z.string().trim().max(160).optional().or(z.literal("")),
  brief: z.record(z.unknown()).optional(),
  attribution: z.unknown().optional(),
  privacyConsent: z.boolean().refine((v) => v === true, {
    message: "Devi accettare la privacy policy per continuare",
  }),
  turnstileToken: z.string().max(4000).optional(),
});
export type InsertFunnelLead = z.infer<typeof insertFunnelLeadSchema>;

// ---------------------------------------------------------------------------
// Stato configurazione tracking (per /api/admin/marketing/config-status).
// "non_configurato" ≠ "configurato ma non verificabile" ≠ "attivo".
// ---------------------------------------------------------------------------

export type ConfigItemState = "attivo" | "configurato_non_verificato" | "non_configurato";

// ---------------------------------------------------------------------------
// Valori storici dell'offerta audit. L'offerta pubblica approvata è ora
// gratuita per tutti gli account: server/routes/marketing-offer.ts ignora
// le vecchie righe DB e rifiuta modifiche tramite la vecchia API.
// ---------------------------------------------------------------------------
export const AUDIT_OFFER_MODES = [
  "non_configurato",
  "gratuito",
  "pagamento",
  "requisiti",
] as const;
export type AuditOfferMode = (typeof AUDIT_OFFER_MODES)[number];

export const auditOfferSchema = z
  .object({
    mode: z.enum(AUDIT_OFFER_MODES),
    // Solo per mode="requisiti": requisiti minimi mostrati (es. spesa mensile).
    requisitiMinimi: z.string().trim().max(400).optional(),
    // Solo per mode="pagamento": indicazione prezzo (testo, es. "da 490 €").
    prezzoIndicativo: z.string().trim().max(120).optional(),
  })
  .strip();
export type AuditOffer = z.infer<typeof auditOfferSchema>;

// ---------------------------------------------------------------------------
// Enhanced Conversions: normalizzazione identificatori secondo le regole
// Google (l'hash SHA-256 avviene SOLO lato server). Nessun invio automatico:
// il sistema prepara i dati, l'upload resta manuale/esterno.
// ---------------------------------------------------------------------------

/** Email normalizzata per Enhanced Conversions; null se non valida. */
export function normalizeEmailForEnhanced(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const email = raw.trim().toLowerCase();
  const m = email.match(/^([^@\s]+)@([^@\s]+\.[^@\s]+)$/);
  if (!m) return null;
  let [, local, domain] = m;
  // Regola Google: per gmail/googlemail rimuovere i punti dalla parte locale.
  if (domain === "gmail.com" || domain === "googlemail.com") {
    local = local.replace(/\./g, "");
  }
  return `${local}@${domain}`;
}

/**
 * Telefono normalizzato E.164 (default Italia). Conservativo: se il numero
 * non è riconducibile con certezza a E.164, ritorna null (meglio nessun dato
 * che un hash sbagliato).
 */
export function normalizePhoneE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[\s\-().]/g, "");
  if (/^\+[1-9]\d{6,14}$/.test(cleaned)) return cleaned;
  if (/^00[1-9]\d{6,14}$/.test(cleaned)) return `+${cleaned.slice(2)}`;
  // Numero nazionale italiano (fisso o mobile): 6–11 cifre, prefisso +39.
  if (/^3\d{8,9}$/.test(cleaned) || /^0\d{5,10}$/.test(cleaned)) return `+39${cleaned}`;
  return null;
}

// ---------------------------------------------------------------------------
// Regole anti-falsi-eventi per le conversioni "orfane" (documentate qui,
// applicate da server/routes):
//  - erp_config_abandoned: SOLO dopo erp_config_start, SOLO se non esiste già
//    erp_config_completed/contact_saved per la stessa sessione; dedupe per
//    sessione (una sola per draft).
//  - erp_demo_richiesta: registrata quando l'utente della demo ERP chiede
//    esplicitamente un contatto/demo reale (non per ogni messaggio chat).
//  - preventivo_qualificato: registrata SOLO alla transizione amministrativa
//    del preventivo verso lo stato "accettato" (vedi adminQuoteStatusValues
//    in shared/schema.ts): è lo stato semanticamente qualificato/accettato.
//    Nessun evento sulle altre transizioni, nessun evento retroattivo;
//    dedupe automatica per preventivo (leadType:quote + leadId + evento).
//  - erp_demo_richiesta: registrata SOLO per i preventivi provenienti dalla
//    pagina ERP conversazionale (metadata/service marcati), mai per i
//    preventivi normali; dedupe automatica per preventivo.
// ---------------------------------------------------------------------------
export const ERP_ABANDON_DEDUP_PREFIX = "erp:abandon:";

export interface MarketingConfigStatus {
  ga4: { state: ConfigItemState; measurementId: string | null };
  gtm: { state: ConfigItemState; containerId: string | null };
  googleAds: {
    state: ConfigItemState;
    conversionId: string | null;
    // Le conversioni offline NON vengono mai inviate da questo sistema:
    // vengono solo preparate per l'export manuale/upload.
    offlineExportReady: boolean;
  };
  turnstile: { state: ConfigItemState };
  consentMode: { state: "attivo" }; // sempre attivo: default denied nel markup
  // Enhanced Conversions: SOLO preparazione hash, mai invio. Stato:
  //  - "non_configurato": toggle off oppure Conversion ID Ads assente;
  //  - "configurato_non_verificato": toggle on + Conversion ID presente.
  // MAI "attivo": l'attivazione reale avviene solo su Google Ads dopo
  // l'upload manuale con consenso utente.
  enhancedConversions: { state: ConfigItemState };
}
